import * as React from "react";
import { useFormContext, useFieldArray } from "react-hook-form";
import { NGLabelText } from "../../../newcomponents/text/labelText";
import { FormDryRunAlert } from "./formDryRunAlert";
import { NGButton } from "../../../newcomponents/button/Button";
import { MinusCircleOutlined } from "@ant-design/icons";
import NGAlert from "../../../newcomponents/alert";
import { FormNGSwitch } from "./components/FormNGSwitch";
import { FormNGSelect } from "./components/FormNGSelect";
import { FormNGKindSelect } from "./components/FormNGKindSelect";
import { DomainInput } from "./types/input";
import { linksOf } from "../../../services/cpln";
import { NGSwitch } from "../../../newcomponents/switch";
import { InfoTooltip } from "../../../components/InfoTooltip";

const allCipherSuiteOptions = [
  "ECDHE-ECDSA-AES256-GCM-SHA384",
  "ECDHE-ECDSA-CHACHA20-POLY1305",
  "ECDHE-ECDSA-AES128-GCM-SHA256",
  "ECDHE-RSA-AES256-GCM-SHA384",
  "ECDHE-RSA-CHACHA20-POLY1305",
  "ECDHE-RSA-AES128-GCM-SHA256",
  "AES256-GCM-SHA384",
  "AES128-GCM-SHA256",
];

interface Props {
  id: string;
  index: number;
}
export const CreateNGAdvancedPortTls: React.FC<Props> = ({ id, index }) => {
  const { control, watch, setValue } = useFormContext<DomainInput>();

  const { fields: cipherSuiteFields, append: appendCipherSuite, remove: removeCipherSuite } = useFieldArray({
    control,
    name: `advanced.spec.ports.${index}.tls.cipherSuites` as any,
  });

  const port = watch(`advanced.spec.ports.${index}`);

  const dnsMode = watch("advanced.spec.dnsMode");
  const routingMode = watch("advanced.spec.routingMode");
  const certChallengeType = watch("advanced.spec.certChallengeType");
  const isTlsEnabled = watch(`advanced.spec.ports.${index}.tls.isEnabled`);

  const isClientEnabled = watch(`advanced.spec.ports.${index}.tls.clientCertificate.isEnabled`);

  const isServerEnabled = watch(`advanced.spec.ports.${index}.tls.serverCertificate.isEnabled`);
  const serverSecret = watch(`advanced.spec.ports.${index}.tls.serverCertificate.secret.name`);

  const cipherSuites = watch(`advanced.spec.ports.${index}.tls.cipherSuites`);

  const cipherSuiteOptions: { label: string; value: string }[] = [];
  for (const cipherSuiteOption of allCipherSuiteOptions) {
    if (cipherSuites.includes(cipherSuiteOption as any)) {
      continue;
    }
    cipherSuiteOptions.push({ label: cipherSuiteOption, value: cipherSuiteOption });
  }

  function addCipherSuite() {
    const optionToUse = cipherSuiteOptions[0].value;
    appendCipherSuite(optionToUse);
  }

  const mustEnableTLS = (!port.number || port.number === "443") && port.protocol !== "tcp";

  return (
    <div key={`port-tls-${id}`} className="mb-8">
      <div className="mb-2">
        <NGLabelText className="cursor-default">TLS</NGLabelText>
      </div>
      <div className="mb-4">
        {mustEnableTLS ? (
          <div className="flex items-center">
            <NGSwitch value={true} isDisabled>
              <NGLabelText>Enable TLS</NGLabelText>
            </NGSwitch>
            <InfoTooltip title={`TLS is always enabled when number is 443 and protocol is either http or http2`} />
          </div>
        ) : (
          <FormNGSwitch name={`advanced.spec.ports.${index}.tls.isEnabled` as const} control={control}>
            <NGLabelText>Enable TLS</NGLabelText>
          </FormNGSwitch>
        )}
      </div>
      <FormDryRunAlert path={`spec.ports[${index}].tls`} />
      {isTlsEnabled || mustEnableTLS ? (
        <>
          <div className="mb-8">
            <div className="flex items-center">
              <NGLabelText className="cursor-default">Min Protocol Version</NGLabelText>
              <span className="color-danger">*</span>
            </div>
            <FormNGSelect
              name={`advanced.spec.ports.${index}.tls.minProtocolVersion` as const}
              control={control}
              rules={{ required: "Protocol version is required" }}
              options={[
                { label: "TLSV1_3", value: "TLSV1_3" },
                { label: "TLSV1_2", value: "TLSV1_2" },
                { label: "TLSV1_1", value: "TLSV1_1" },
                { label: "TLSV1_0", value: "TLSV1_0" },
              ]}
              style={{ maxWidth: 450 }}
            />
            <FormDryRunAlert path={`spec.ports[${index}].tls.minProtocolVersion`} />
          </div>
          <div className="mb-8">
            <FormNGSwitch
              name={`advanced.spec.ports.${index}.tls.clientCertificate.isEnabled` as const}
              control={control}
              className="mb-2"
            >
              <NGLabelText>Forward Client Certificate</NGLabelText>
            </FormNGSwitch>
            {isClientEnabled ? (
              <>
                <div>
                  <NGLabelText className="cursor-default">Client Certificate Authority PEM</NGLabelText>
                  <FormNGKindSelect
                    name={`advanced.spec.ports.${index}.tls.clientCertificate.secret.name` as const}
                    optionValueAs="name"
                    control={control}
                    kind={"secret"}
                    queries={[{ property: "type", value: "tls" }]}
                    style={{ maxWidth: 600 }}
                    onChangeExtra={(_, item) => {
                      const selfLink = linksOf(item).self!;
                      setValue(`advanced.spec.ports.${index}.tls.clientCertificate.secret.link`, selfLink);
                    }}
                  />
                </div>
              </>
            ) : null}
            <FormDryRunAlert
              path={`spec.ports[${index}].tls.clientCertificate`}
              paths={[`spec.ports[${index}].tls.clientCertificate.secretLink`]}
            />
          </div>
          <div className="mb-8">
            <FormNGSwitch
              name={`advanced.spec.ports.${index}.tls.serverCertificate.isEnabled` as const}
              control={control}
              className="mb-2"
            >
              <NGLabelText>Use Custom Server Certificate</NGLabelText>
            </FormNGSwitch>
            {isServerEnabled ? (
              <>
                <div>
                  <div className="flex items-center">
                    <NGLabelText className="cursor-default">Server Certificate Authority PEM</NGLabelText>
                    {routingMode === "gvcSubdomain" && dnsMode === "cname" ? (
                      <span className="color-danger">*</span>
                    ) : null}
                  </div>
                  <FormNGKindSelect
                    name={`advanced.spec.ports.${index}.tls.serverCertificate.secret.name` as const}
                    optionValueAs="name"
                    control={control}
                    kind={"secret"}
                    queries={[{ property: "type", value: "tls" }]}
                    style={{ maxWidth: 600 }}
                    onChangeExtra={(_, item) => {
                      const selfLink = linksOf(item).self!;
                      setValue(`advanced.spec.ports.${index}.tls.serverCertificate.secret.link`, selfLink);
                    }}
                  />
                </div>
              </>
            ) : null}
            {routingMode === "gvcSubdomain" &&
            dnsMode === "cname" &&
            certChallengeType === "http01" &&
            !serverSecret ? (
              <NGAlert
                className="mt-2"
                type={"error"}
                title="Requirement"
                message={`Using CNAME and GVC Subdomain requires Server Cert Auth PEM to be set on all ports with tls enabled.`}
              />
            ) : null}
            <FormDryRunAlert
              path={`spec.ports[${index}].tls.serverCertificate`}
              paths={[`spec.ports[${index}].tls.serverCertificate.secretLink`]}
            />
          </div>
          <div>
            <div className="flex items-center gap-4 mb-2">
              <NGLabelText className="cursor-default">Cipher Suites</NGLabelText>
              <NGButton
                variant="primary"
                size={"small"}
                onClick={addCipherSuite}
                disabled={cipherSuiteOptions.length < 1}
              >
                Add
              </NGButton>
            </div>
            {cipherSuites.length < 1 ? <div>No Items</div> : null}
            {cipherSuiteFields.map((field, cipherIndex) => {
              return (
                <div key={field.id} className="mb-1">
                  <div className="flex items-center gap-2">
                    <FormNGSelect
                      name={`advanced.spec.ports.${index}.tls.cipherSuites.${cipherIndex}` as const}
                      control={control}
                      options={cipherSuiteOptions}
                      style={{ maxWidth: 450 }}
                    />
                    {cipherSuiteFields.length > 1 ? (
                      <MinusCircleOutlined className="color-danger" onClick={() => removeCipherSuite(cipherIndex)} />
                    ) : null}
                  </div>
                </div>
              );
            })}
            <FormDryRunAlert
              path={`spec.ports[${index}].tls.cipherSuites`}
              paths={cipherSuites.map((_, cipherIndex) => `spec.ports[${index}].tls.cipherSuites[${cipherIndex}]`)}
            />
          </div>
        </>
      ) : null}
    </div>
  );
};
